/**
 * External dependencies
 */
import type {
	ConversionEvent,
	ExperimentId,
	Maybe,
	TrackEvent,
	UniqueConversionEvent,
	UniqueVisitEvent,
	VisitEvent,
} from '@nab/types';
import type { Uuid } from 'uuid';

/**
 * Internal dependencies
 */
import { getApiUrl } from '../utils/helpers';
import { getActiveSegments } from '../utils/segmentation';
import {
	canSynchEvent,
	getUniqueViews,
	updateExperimentsWithPageViews,
	updateUniqueViews,
} from '../utils/tracking';

import type {
	ConvertingGoal,
	GoalIndex,
	HeatmapSummary,
	Session,
} from '../types';

export function sendViews(
	expIds: ExperimentId | ReadonlyArray< ExperimentId >,
	session: Session
): void {
	const events = ( isArray( expIds ) ? expIds : [ expIds ] )
		.map( ( id ) => generatePageViewEvent( id, session ) )
		.filter( isVisitEvent );

	track( events, session );
}

export function sendConversions(
	convGoals: ConvertingGoal | ReadonlyArray< ConvertingGoal >,
	session: Session
): void {
	const { experiments } = session;
	const isConvEvent = ( e?: ConversionEvent ): e is ConversionEvent => !! e;
	const events = ( isArray( convGoals ) ? convGoals : [ convGoals ] )
		.map( ( { experiment, goal } ) =>
			generateConversionEvent( experiment, goal, experiments )
		)
		.filter( isConvEvent );

	track( events, session );
}

export function track(
	events: ReadonlyArray< TrackEvent >,
	session: Session
): void {
	if ( ! events.length ) {
		return;
	}

	session.hasQuota
		.then( ( hasQuota ) => {
			if ( ! hasQuota ) {
				console.info( '[NAB] No more quota available.' ); //eslint-disable-line
				return;
			}

			events = events.filter( ( ev ) => canSynchEvent( ev, session ) );
			updateExperimentsWithPageViews(
				events.filter( isVisitEvent ),
				session
			);
			events = addUniqueEvents( events );
			if ( ! events.length ) {
				return;
			}

			const { api, site, timezone } = session;
			const timestamp = new Date().toISOString();
			events = events.map( ( event ) => ( {
				id: crypto.randomUUID() as Uuid,
				...event,
				timezone,
				timestamp,
				_uvisit: getUniqueViews()[ event.experiment ],
			} ) );

			events.forEach( ( event ) =>
				window.dispatchEvent(
					new CustomEvent( 'nelio-ab-testing/track', {
						detail: { event: { ...event } },
					} )
				)
			);

			if ( session.isStagingSite ) {
				events.forEach( ( event ) =>
					// eslint-disable-next-line no-console
					console.info( '[NAB] Staging Event', event )
				);
				return;
			}

			const url = getApiUrl( api, `/site/${ site }/event`, {
				e: window.btoa( JSON.stringify( events ) ),
				a: site,
			} );

			/* eslint-disable */
			const beaconSent =
				!! session.useSendBeacon &&
				'function' === typeof window.navigator.sendBeacon &&
				!! window.Blob &&
				window.navigator.sendBeacon(
					url,
					new Blob( [], { 'Content-Type': 'text/plain' } as any )
				);
			/* eslint-enable */

			if ( ! beaconSent ) {
				const image = document.createElement( 'img' );
				image.setAttribute( 'src', url );
			}
		} )
		.catch( () => {
			console.info( '[NAB] No more quota available (2).' ); //eslint-disable-line
		} );
}

// =======
// HELPERS
// =======

const isArray = < T >( a: T | ReadonlyArray< T > ): a is ReadonlyArray< T > =>
	Array.isArray( a );

function generatePageViewEvent(
	experimentId: ExperimentId,
	session: Session
): Maybe< VisitEvent > {
	const { experiments, heatmaps } = session;

	const experiment = experiments.find( ( e ) => e.id === experimentId );
	if ( ! experiment ) {
		return maybeGeneratePageViewEventForHeatmap( experimentId, heatmaps );
	}

	const segments = getActiveSegments( experiment );
	if ( ! segments.length ) {
		return;
	}

	const type = getVisitType( experimentId, session.experiments );
	if ( ! type ) {
		return;
	}

	const alternative = experiment.alternative;

	return {
		kind: 'visit',
		experiment: experimentId,
		type,
		alternative,
		segments,
	};
}

function getVisitType(
	experimentId: ExperimentId,
	experiments: Session[ 'experiments' ]
): Maybe< VisitEvent[ 'type' ] > {
	const experiment = experiments.find( ( e ) => e.id === experimentId );
	if ( ! experiment ) {
		return;
	}

	switch ( experiment.type ) {
		case 'nab/wc-bulk-sale':
		case 'nab/wc-product':
			return 'woocommerce';

		case 'nab/css':
		case 'nab/headline':
		case 'nab/menu':
		case 'nab/template':
		case 'nab/theme':
		case 'nab/widget':
			return 'global';

		default:
			return 'regular';
	}
}

function maybeGeneratePageViewEventForHeatmap(
	experimentId: ExperimentId,
	heatmaps: ReadonlyArray< HeatmapSummary >
): Maybe< VisitEvent > {
	const ids = heatmaps.map( ( { id } ) => id );
	if ( ! ids.includes( experimentId ) ) {
		return;
	}

	return {
		kind: 'visit',
		experiment: experimentId,
		type: 'regular',
		alternative: 0,
		segments: [],
	};
}

function generateConversionEvent(
	experimentId: ExperimentId,
	goalIndex: GoalIndex,
	experiments: Session[ 'experiments' ]
): Maybe< ConversionEvent > {
	const experiment = experiments.find( ( e ) => e.id === experimentId );
	if ( ! experiment ) {
		return;
	}

	if ( experiment.goals.length <= goalIndex ) {
		return;
	}

	const segments = getActiveSegments( experiment );
	if ( ! segments.length ) {
		return;
	}

	const alternative = experiment.alternative;
	return {
		kind: 'conversion',
		experiment: experimentId,
		alternative,
		goal: goalIndex,
		segments,
	};
}

function isVisitEvent( e?: TrackEvent ): e is VisitEvent {
	return !! e && e.kind === 'visit';
}

function addUniqueEvents(
	events: ReadonlyArray< TrackEvent >
): ReadonlyArray< TrackEvent > {
	const uniqueVisits = events
		.filter( ( e ) => e.kind === 'visit' )
		.map( makeUniqueViewEvent )
		.filter( isDefined );

	updateUniqueViews( uniqueVisits );

	const uniqueConversions = events
		.filter( ( e ) => e.kind === 'conversion' )
		.map( makeUniqueConversionEvent )
		.filter( isDefined );

	return [ ...events, ...uniqueVisits, ...uniqueConversions ];
}

function makeUniqueViewEvent( visit: VisitEvent ): Maybe< UniqueVisitEvent > {
	const uniqueViews = getUniqueViews();
	return uniqueViews[ visit.experiment ]
		? undefined
		: { ...visit, kind: 'uvisit', id: crypto.randomUUID() as Uuid };
}

function makeUniqueConversionEvent(
	conversion: ConversionEvent
): Maybe< UniqueConversionEvent > {
	const uniqueViews = getUniqueViews();
	const uniqueViewId = uniqueViews[ conversion.experiment ];
	return ! uniqueViewId
		? undefined
		: {
				...conversion,
				kind: 'uconversion',
				id: `${ uniqueViewId }-${ conversion.goal }`,
		  };
}

// DEPRECATED. Remove “canMonitorEvents” eventually.
/* eslint-disable */
const canMonitorEvents = (
	win: unknown
): win is {
	nabMonitorTrackingEvents: ( events: ReadonlyArray< TrackEvent > ) => void;
} => !! win && typeof win === 'object' && 'nabMonitorTrackingEvents' in win;
window.addEventListener( 'message', ( ev ) => {
	if (
		ev.data?.plugin !== 'nelio-ab-testing' ||
		ev.data?.type !== 'testing-event' ||
		! ev.data?.value
	) {
		return;
	}
	if ( canMonitorEvents( window ) ) {
		console.groupCollapsed( 'Deprecation Warning!' );
		console.log(
			'Nelio A/B Testing deprecated “nabMonitorTrackingEvents”.'
		);
		console.log(
			"Use “window.addEventListener( 'nelio-ab-testing/track', callback )” instead and the event from “details.event”."
		);
		console.groupEnd();
		window.nabMonitorTrackingEvents( [ ev.data.value ] );
	}
} );
/* eslint-enable */

const isDefined = < T >( x: T | undefined ): x is T => undefined !== x;
